/**
 * Helpers for working with bug-report data blobs.
 *
 * 2026-05-01: Dev-edit overlay. The dev can override title/description/
 * tags/importance after submission (joke mitigation, criticality
 * recalibration, clarification). The reporter's original is preserved
 * on the top-level fields; the dev's overrides land on `data.devEdits`.
 *
 * `effectiveData(parsed, viewer)` returns the merged view a given
 * viewer should see:
 *   - 'reporter' viewer: always the original (devEdits stripped). Avoids
 *     "why did my report change?" confusion.
 *   - 'dev' viewer + 'export' context: prefer devEdits over original.
 */

export const safeParse = (s) => {
  if (typeof s !== 'string' || !s) return {};
  try { return JSON.parse(s) || {}; } catch { return {}; }
};

const OVERRIDE_KEYS = ['title', 'description', 'tags', 'importance', 'importanceRaw', 'importanceScale'];

// `viewer` is one of: 'reporter' | 'dev' | 'export'. 'dev' and 'export'
// behave the same in terms of merging — the dev's edits become the
// authoritative version. 'reporter' always sees their original.
export const effectiveData = (parsed, viewer = 'reporter') => {
  if (!parsed || typeof parsed !== 'object') return parsed || {};
  if (viewer === 'reporter') return parsed;
  const overrides = parsed.devEdits;
  if (!overrides) return parsed;
  const out = { ...parsed };
  for (const k of OVERRIDE_KEYS) {
    if (k in overrides) out[k] = overrides[k];
  }
  return out;
};

export const hasDevEdits = (parsed) => {
  if (!parsed?.devEdits) return false;
  return OVERRIDE_KEYS.some(k => k in parsed.devEdits);
};

export const isExcludedFromExport = (parsed) => !!parsed?.excludeFromExport;
